import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { FiLogOut } from "react-icons/fi";
import Events from "./Events/Events";
import Sidebar from "./SideBar/Sidebar";
import Dashboard from "./DashBoard/Dashboard";
import ContactMessages from "./ContactUs/ContactMessages";
import JobPostings from "./JobPosting/JobPostings";
import JobApplications from "./JobApplications/JobApplications";
import HeroSection from "./HeroSection/HeroSection";
import Sections from "./Section/Sections";
import Subsections from "./SubSections/Subsections";
import "./AdminPortal.css";

// User Management
import SuperAdminUsers from "../Admins/SuperAdminUsers/SuperAdminUsers";
import AdminUsers from "../Admins/AdminUsers/AdminUsers";
import Users from "../Admins/Users/Users";
import Roles from "../Admins/ApplicationRoles/ApplicationRoles";
import Privileges from "../Admins/ApplicationPrivileges/ApplicationPrivileges";
import Tenants from "../Admins/Tentants/Tenants";
import Applications from "../Admins/Application/Application";
import TenantApplications from "../Admins/TentantApplication/TenantApplication";
import UserApplications from "../Admins/UserApplicationRoles/UserApplicationRoles";
import Axios from "../../utils/axios";

const normalizeTenant = (tenant) => {
  if (!tenant) return null;
  const secret = tenant.publicKey || tenant.secretKey || null;
  return {
    ...tenant,
    publicKey: secret,
    secretKey: secret,
  };
};

const isSuperAdminUser = (user) => {
  if (!user) return false;
  const username = user.username?.toLowerCase();
  if (username === "superadmin" || username === "global") return true;
  return (user.applications || []).some(
    (app) => app.roleName?.toUpperCase() === "SUPERADMIN"
  );
};

const AdminPortal = () => {
  const { logout, user, selectTenant } = useAuth();
  const [activeMenuItem, setActiveMenuItem] = useState("dashboard");
  const [sectionsData, setSectionsData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const isSuperAdmin = isSuperAdminUser(user);

  const applySelectedTenant = (tenant) => {
    const normalized = normalizeTenant(tenant);
    setSelectedTenant(normalized);
    selectTenant(normalized);
  };

  const fetchTenants = async () => {
    try {
      if (!isSuperAdmin) return;

      const response = await Axios.get("/api/tenant/getAllTenants");

      // handle API structure
      const data = response.data?.data || response.data || [];
      const normalizedTenants = (Array.isArray(data) ? data : [data])
        .filter(Boolean)
        .map(normalizeTenant);

      setTenants(normalizedTenants);

      // Restore from localStorage or select default (include GLOBAL)
      const storedTenant = localStorage.getItem("selectedTenant");

      if (storedTenant) {
        const parsed = normalizeTenant(JSON.parse(storedTenant));
        const refreshed =
          normalizedTenants.find((t) => t.id === parsed?.id) || parsed;
        applySelectedTenant(refreshed);
      } else if (normalizedTenants.length > 0) {
        applySelectedTenant(normalizedTenants[0]);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants();
    } else if (user?.tenantName) {
      // For normal user — load their tenant (with secret key when available)
      applySelectedTenant({
        name: user.tenantName,
        publicKey: user.publicKey || user.secretKey || null,
        secretKey: user.secretKey || user.publicKey || null,
      });
    }
  }, [user]);

  const handleTenantChange = (event) => {
    const selectedId = parseInt(event.target.value, 10);
    const tenant = tenants.find((t) => t.id === selectedId);
    if (tenant) {
      applySelectedTenant(tenant);
      window.location.reload();
    }
  };

  const renderContent = () => {
    switch (activeMenuItem) {
      case "dashboard":
        return <Dashboard />;
      case "hero-section":
        return <HeroSection />;
      case "sections":
        return (
          <Sections
            setSectionsData={setSectionsData}
            sectionsData={sectionsData}
          />
        );
      case "subsections":
        return <Subsections sectionsData={sectionsData} />;
      case "events":
        return <Events setEventsData={setEventsData} eventsData={eventsData} />;
      case "contact-us":
        return <ContactMessages />;
      case "job-postings":
        return <JobPostings />;
      case "job-applications":
        return <JobApplications />;
      case "super-admin-users":
        return <SuperAdminUsers />;
      case "admin-users":
        return <AdminUsers />;
      case "users":
        return <Users />;
      case "roles":
        return <Roles />;
      case "privileges":
        return <Privileges />;
      case "tenants":
        return <Tenants />;
      case "applications":
        return <Applications />;
      case "tenant-applications":
        return <TenantApplications />;
      case "user-applications":
        return <UserApplications />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="admin-portal-container">
      <Sidebar
        activeMenuItem={activeMenuItem}
        setActiveMenuItem={setActiveMenuItem}
      />

      <div className="admin-main-content">
        <header className="admin-header">
          <div
            className="tenant-selector"
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "20px",
              flex: 1,
            }}
          >
            <label
              style={{
                fontWeight: "700",
                fontSize: "18px",
                color: "#2a4e85",
                marginRight: "8px",
              }}
            >
              Selected Tenant :
            </label>

            {isSuperAdmin && tenants.length > 0 ? (
              <select
                id="tenantSelect"
                value={selectedTenant?.id || ""}
                onChange={handleTenantChange}
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: "#f9f9f9",
                  color: "#333",
                  fontSize: "15px",
                  fontWeight: "500",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  outline: "none",
                  minWidth: "220px",
                }}
                onFocus={(e) => {
                  e.target.style.border = "1px solid #4A90E2";
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                }}
                onBlur={(e) => {
                  e.target.style.border = "1px solid #ddd";
                  e.target.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                }}
              >
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant?.name}
                  </option>
                ))}
              </select>
            ) : (
              <span
                style={{
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  backgroundColor: "#f9f9f9",
                  color: "#333",
                  fontSize: "15px",
                  fontWeight: "500",
                  boxShadow: "0 1px 1px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  outline: "none",
                  minWidth: "220px",
                }}
              >
                {selectedTenant?.name || user?.tenantName || ""}
              </span>
            )}
          </div>
          <div className="admin-header-left"></div>

          <div className="admin-header-right">
            <div className="user-info">
              Welcome, <strong>{user?.fullName || user?.username || ""}</strong>
            </div>
            <button onClick={logout} className="logout-btn">
              <FiLogOut size={20} /> Logout
            </button>
          </div>
        </header>

        <main className="admin-content-area">{renderContent()}</main>
      </div>
    </div>
  );
};

export default AdminPortal;
